import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import RichTextEditor from '@/components/RichTextEditor';
import {
  RefreshCw,
  Plus,
  MessageSquare,
  MapPin,
  User,
  Calendar,
  Paperclip,
  StickyNote,
  Ticket,
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronRight,
  ChevronLeft,
  Reply,
  ReplyAll,
  Forward,
  X,
  Filter,
  Search,
  MoreVertical,
  UserRound,
  Pencil,
  Maximize2,
  Minimize2,
  ImagePlus,
  Trash2,
  Download,
  LayoutGrid,
  List as ListIcon,
  BarChart3,
} from 'lucide-react';

import { API_BASE_URL, BACKEND_BASE_URL } from '@/lib/apiBase';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { PullToRefreshIndicator } from '@/components/PullToRefreshIndicator';
import { useSwipeActions } from '@/hooks/useSwipeActions';

// Import extracted ticket components
import {
  stripHtml,
  formatDateCompact,
  formatDateTime,
  formatRelativeTime,
  StatusBadge,
  PriorityBadge,
  SlaBadge,
  AttachmentBlock,
  ConversationMessage,
  TicketKanban,
  AllAttachmentsDialog,
  TicketDashboard,
  TicketListItem,
  TicketDetailSidebar,
  TicketDetailHeader,
  CreateTicketDialog,
  TicketComposerPane,
  sanitizeAttachmentHtml,
} from '@/components/tickets';
import {
  internalStatusBadgeClasses,
  formatDueDate,
  dueDateRelative,
} from '@/lib/ticketFields';

const API = API_BASE_URL;

/**
 * Wraps a shadcn TableRow with horizontal-swipe gestures.
 * Swipe right → onSwipeRight (e.g. assign to me), swipe left → onSwipeLeft (e.g. resolve).
 */
const SwipeTableRow = ({ onSwipeLeft, onSwipeRight, children, ...rowProps }) => {
  const swipe = useSwipeActions({ onSwipeLeft, onSwipeRight });
  return (
    <TableRow {...rowProps} {...swipe}>
      {children}
    </TableRow>
  );
};

const TicketsInboxPage = () => {
  const { user, isAdmin, isStaff, isAgencyAdmin } = useAuth();
  // "Agency staff" = anyone with agency-wide power (agency admin OR agency support).
  const isAgencyStaff = useCallback(
    () => ['agency_admin', 'agency_support', 'super_admin', 'admin', 'support_agent'].includes(user?.role),
    [user],
  );
  const [loading, setLoading] = useState(true);
  const [assignees, setAssignees] = useState([]);

  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [showTicketView, setShowTicketView] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(true);
  // Mobile uses a separate drawer state so the right panel doesn't auto-block the view.
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [showComposerOnly, setShowComposerOnly] = useState(false);

  const [locations, setLocations] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);

  const [stats, setStats] = useState(null);
  const [locationStats, setLocationStats] = useState([]);

  // filters
  const [filterStatus, setFilterStatus] = useState('not_resolved'); // open | in_progress | waiting_on_customer | under_investigation | resolved | all | not_resolved | recent_reply
  // High-level tab — controls filterStatus + grouping for the list view.
  // 'open' = Not Resolved + group by status; 'resolved' = only resolved, no grouping.
  const [openTab, setOpenTab] = useState('open');
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterInternalStatus, setFilterInternalStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  // Mobile: collapse detailed filters behind a toggle button
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // view mode
  const [searchParams, setSearchParams] = useSearchParams();
  const viewFromUrl = searchParams.get('view');
  const [viewMode, setViewMode] = useState(() => {
    if (viewFromUrl === 'list' || viewFromUrl === 'kanban' || viewFromUrl === 'dashboard') {
      return viewFromUrl;
    }
    return 'dashboard';
  });

  // Sync URL -> state when user navigates via the sidebar
  useEffect(() => {
    if (viewFromUrl && viewFromUrl !== viewMode &&
        (viewFromUrl === 'list' || viewFromUrl === 'kanban' || viewFromUrl === 'dashboard')) {
      setViewMode(viewFromUrl);
      // Switching view via the sidebar should close any open ticket detail.
      // Otherwise the detail stays mounted and visually overrides the new view.
      setShowTicketView(false);
      setShowComposerOnly(false);
      setSelectedTicketId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFromUrl]);

  // Sync state -> URL (so the sidebar highlight reflects the toggle)
  useEffect(() => {
    if (viewFromUrl !== viewMode) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', viewMode);
        return next;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const [kanbanGroupBy, setKanbanGroupBy] = useState('status'); // status | priority | assignee
  const [listGroupBy, setListGroupBy] = useState('status'); // none | status | priority | assignee
  const [listGroupFilter, setListGroupFilter] = useState('all'); // 'all' or a specific group key

  // Filters strip is hidden by default on desktop too now — user toggles it.
  const [showFilters, setShowFilters] = useState(false);

  // Apply tab → filterStatus + grouping rules.
  const handleSelectOpenTab = useCallback((tab) => {
    setOpenTab(tab);
    if (tab === 'open') {
      setFilterStatus('not_resolved');
      setListGroupBy('status');
      setListGroupFilter('all');
    } else {
      setFilterStatus('resolved');
      setListGroupBy('none');
      setListGroupFilter('all');
    }
  }, []);

  // Left ticket list panel collapsed (in 3-panel view) — allows the center ticket view to take full width
  const [ticketsListCollapsed, setTicketsListCollapsed] = useState(() => {
    try { return localStorage.getItem('tickets_list_collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('tickets_list_collapsed', ticketsListCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [ticketsListCollapsed]);

  // all attachments dialog
  const [showAllAttachments, setShowAllAttachments] = useState(false);

  // modules list for inline editor in detail view
  const [availableModulesForEdit, setAvailableModulesForEdit] = useState([]);

  // internal statuses (from Settings)
  const [internalStatuses, setInternalStatuses] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/internal-statuses`);
        setInternalStatuses(data?.items || []);
      } catch {
        /* ignore — non-critical */
      }
    })();
  }, []);

  // create ticket
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTicket, setNewTicket] = useState({
    location_id: '',
    subject: '',
    module: '',
    text: '',
    priority: 'medium',
    reported_person_name: '',
    attachments: [],
    followers: [],
    on_behalf_of_user_id: '',
    internal_only: false,
  });

  // saved email contacts for CC/BCC autocomplete (req: save manual cc/bcc adds)
  const [emailContacts, setEmailContacts] = useState([]);
  const fetchEmailContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email-contacts`);
      setEmailContacts((res.data?.items || []).map((x) => x.email).filter(Boolean));
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => { fetchEmailContacts(); }, [fetchEmailContacts]);

  // Sender identities (From-picker options) — fetched from settings; every staff user reads.
  const [senderIdentities, setSenderIdentities] = useState([]);
  const [senderIdentity, setSenderIdentity] = useState('');
  // Reply-as: staff-only. When set, the outgoing reply is authored + signed by the target user.
  const [replyAsUserId, setReplyAsUserId] = useState('');
  // Staff assignees list (already fetched separately) doubles as the "Reply-as" candidate list.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/sender-identities`);
        const list = data?.identities || [];
        setSenderIdentities(list);
        if (list[0]?.email && !senderIdentity) setSenderIdentity(list[0].email);
      } catch { /* not fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Signature helpers ---------------------------------------------------
  // The signature is wrapped in a data-signature="1" block so the "Reply as"
  // dropdown can locate it and swap in the target user's signature without
  // disturbing anything the agent has typed above or below it.
  const escapeHtml = (s) => String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const buildSignatureBlock = (sigRaw) => {
    const trimmed = (sigRaw || '').trim();
    if (!trimmed) return '';
    const inner = trimmed
      .split('\n')
      .map((line) => `<p>${escapeHtml(line) || '<br/>'}</p>`)
      .join('');
    return `<div data-signature="1">${inner}</div>`;
  };

  // Replace the existing signature marker with `nextBlock`. If no marker is
  // found (e.g. because the user manually deleted it), append the new block
  // to the end of the draft so the picked user's signature still shows up.
  const swapSignatureBlock = (html, nextBlock) => {
    const cur = html || '';
    const re = /<div\s+data-signature=("1"|'1')[^>]*>[\s\S]*?<\/div>/i;
    if (re.test(cur)) {
      return cur.replace(re, nextBlock || '');
    }
    // Marker missing → append (still preserve the rest of the composed text).
    if (!nextBlock) return cur;
    return `${cur}${nextBlock}`;
  };



  // Reply snippets + ticket templates (canned bodies authored under Settings → Snippets).


  const [replySnippets, setReplySnippets] = useState([]);
  const [ticketTemplates, setTicketTemplates] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/snippets?type=reply`),
          axios.get(`${API}/snippets?type=ticket_template`),
        ]);
        setReplySnippets(r1.data || []);
        setTicketTemplates(r2.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  // AI suggest-reply call (composer → backend → GPT-4.1-mini).
  // - If the editor body has substantive content beyond the auto-injected signature,
  //   we send it as `draft` and ask the model to POLISH it.
  // - Otherwise we ask the model to GENERATE a reply from the ticket conversation.
  // Note: we read `replyText` through a ref to avoid a temporal-dead-zone error
  // (the useState for replyText is declared further down in the component).
  const [aiBusy, setAiBusy] = useState(false);
  const replyTextRef = useRef('');
  const handleAiSuggest = useCallback(async ({ tone = 'professional', instruction = '' } = {}) => {
    if (!selectedTicketId) return;
    setAiBusy(true);
    try {
      // Detect whether the user has typed a real draft on top of the signature.
      // Cheap heuristic: strip tags from the current body AND the signature,
      // subtract the signature characters, and check what's left.
      const currentText = replyTextRef.current || '';
      const currentPlain = currentText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      const sigPlain = (user?.email_signature || '').replace(/\s+/g, ' ').trim();
      const residual = sigPlain ? currentPlain.replace(sigPlain, '').trim() : currentPlain;
      const hasDraft = residual.length > 10;

      const res = await axios.post(`${API}/ai/suggest-reply`, {
        ticket_id: selectedTicketId,
        tone,
        instruction,
        draft: hasDraft ? currentText : '',
      });
      const html = res.data?.html || '';
      const mode = res.data?.mode || 'generate';
      if (!html) {
        toast.error('AI returned an empty draft');
        return;
      }
      if (mode === 'polish') {
        // Replace the user's draft with the polished version, but keep the
        // pre-filled signature underneath (if any) so they don't lose it.
        const sigRaw = (user?.email_signature || '').trim();
        const sigHtml = sigRaw
          ? `<p></p>${sigRaw
              .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
              .split('\n').map((line) => `<p>${line || '<br/>'}</p>`).join('')}`
          : '';
        setReplyText(`${html}${sigHtml}`);
        toast.success('Draft polished');
      } else {
        // Generate mode: insert at the top, keeping anything (e.g. signature) the user had.
        setReplyText((prev) => (prev && prev.trim() ? `${html}${prev}` : html));
        toast.success('AI draft inserted');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI request failed');
    } finally {
      setAiBusy(false);
    }
  }, [selectedTicketId, user]);

  // reply / forward composer
  const [replyText, setReplyText] = useState('');
  // Keep the ref handleAiSuggest uses in sync with the live state.
  useEffect(() => { replyTextRef.current = replyText; }, [replyText]);
  const [replying, setReplying] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const [replyMode, setReplyMode] = useState('reply'); // reply | reply_all | forward
  const [composeTo, setComposeTo] = useState('');
  const [composeCc, setComposeCc] = useState('');
  const [composeBcc, setComposeBcc] = useState('');
  const [showRecipientsExpanded, setShowRecipientsExpanded] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  
  // Composer enhancements
  const [composerFullscreen, setComposerFullscreen] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);
  // 'reply' sends email; 'public_note' and 'private_note' are silent comments.
  // public_note = visible to customer; private_note = staff only.
  const [replyKind, setReplyKind] = useState('reply');

  // When the "Reply as" dropdown changes, hot-swap only the signature block in
  // the composer — the agent's typed message above/below the signature is
  // preserved verbatim. Falls back to the logged-in user's signature when the
  // picker is cleared ("Myself"). Runs only after the composer is open so we
  // don't force a stale signature into a fresh draft.
  useEffect(() => {
    if (!showComposer) return;
    if ((replyKind || 'reply').endsWith('note')) return;
    let targetSig = user?.email_signature || '';
    if (replyAsUserId) {
      const target = (assignees || []).find((a) => a?.id === replyAsUserId);
      targetSig = (target && target.email_signature) || '';
    }
    const nextBlock = buildSignatureBlock(targetSig);
    setReplyText((prev) => swapSignatureBlock(prev, nextBlock));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyAsUserId, showComposer]);

  // Optional: set ticket status to this value AFTER sending the reply.
  // Empty string = leave as-is.
  const [statusAfterSend, setStatusAfterSend] = useState('');

  const [draftSubject, setDraftSubject] = useState('');
  const [editingSubject, setEditingSubject] = useState(false);

  const selectedTicket = useMemo(
    () => tickets.find((t) => t.id === selectedTicketId) || null,
    [tickets, selectedTicketId]
  );

  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== 'all' && filterStatus !== 'not_resolved' && filterStatus !== 'recent_reply') params.append('status', filterStatus);
      if (filterLocation && filterLocation !== 'all') params.append('location_id', filterLocation);
      if (filterPriority && filterPriority !== 'all') params.append('priority', filterPriority);
      if (dateFrom) params.append('start_date', `${dateFrom}T00:00:00.000Z`);
      if (dateTo) params.append('end_date', `${dateTo}T23:59:59.999Z`);

      const response = await axios.get(`${API}/tickets?${params.toString()}`);
      setTickets(response.data || []);
    } catch (e) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterLocation, filterPriority, dateFrom, dateTo]);

  const fetchTicketDetail = useCallback(async (ticketId) => {
    if (!ticketId) return;
    try {
      const res = await axios.get(`${API}/tickets/${ticketId}`);
      const t = res.data;
      if (t?.customer_replied_unread && isStaff) {
        toast.info(`Reopened — ${t.created_by_name || 'customer'} replied`, {
          description: `Ticket #${t.ticket_number} was moved back to Open.`,
          duration: 6000,
        });
      }
      setTickets((prev) => prev.map((x) => (x.id === ticketId ? t : x)));
    } catch (e) {
      // ignore
    }
  }, [isStaff]);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data || []);
    } catch (e) {
      // ignore
    }
  }, []);

  const fetchModules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/ticket-modules`);
      const list = response.data.modules || [];
      setAvailableModules(list);
      setAvailableModulesForEdit(list);
    } catch (e) {
      const fallback = [
        'Calendar Sync',
        'Field Mapping',
        'User Management',
        'Contacts',
        'Offboarding',
        'General Support',
        'Bug Report',
        'Feature Request',
      ];
      setAvailableModules(fallback);
      setAvailableModulesForEdit(fallback);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      if (!isStaff()) return;
      const params = new URLSearchParams();
      if (filterLocation && filterLocation !== 'all') params.append('location_id', filterLocation);
      if (filterPriority && filterPriority !== 'all') params.append('priority', filterPriority);
      if (dateFrom) params.append('start_date', `${dateFrom}T00:00:00.000Z`);
      if (dateTo) params.append('end_date', `${dateTo}T23:59:59.999Z`);
      const response = await axios.get(`${API}/tickets/stats/summary?${params.toString()}`);
      const data = response.data || {};

      if (data.closed !== undefined && data.waiting_on_customer === undefined) {
        data.waiting_on_customer = data.closed;
        delete data.closed;
      }
      setStats(data);
    } catch (e) {
      // ignore
    }
  }, [isStaff, filterLocation, filterPriority, dateFrom, dateTo]);

  const fetchAssignees = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets/assignees`);
      setAssignees(res.data || []);
    } catch (e) {
      // ignore
    }
  }, []);

  const fetchLocationStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterPriority && filterPriority !== 'all') params.append('priority', filterPriority);
      if (dateFrom) params.append('start_date', `${dateFrom}T00:00:00.000Z`);
      if (dateTo) params.append('end_date', `${dateTo}T23:59:59.999Z`);
      const response = await axios.get(`${API}/tickets/stats/by-location?${params.toString()}`);
      setLocationStats(response.data || []);
    } catch (e) {
      // ignore
    }
  }, [filterPriority, dateFrom, dateTo]);

  const notResolvedCount = useMemo(() => {
    if (!stats) return 0;
    return (stats.open || 0) + (stats.in_progress || 0) + (stats.waiting_on_customer || 0);
  }, [stats]);

  const recentReplyCount = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return (tickets || []).filter((t) => {
      const replies = Array.isArray(t.replies) ? t.replies : [];
      if (replies.length === 0) return false;
      const last = replies[replies.length - 1];
      const ts = new Date(last?.created_at || t.updated_at || 0).getTime();
      return ts >= cutoff;
    }).length;
  }, [tickets]);

  const handleClickStatusPill = (status) => {
    setFilterStatus(status);
  };

  const handleClickTotal = () => {
    setFilterStatus('all');
  };

  const handleClickNotResolved = () => {
    // Not resolved = everything except resolved
    setFilterStatus('not_resolved');
  };

  const handleClickLocationNotResolved = (locationId) => {
    setFilterLocation(locationId);
    setFilterStatus('not_resolved');
  };

  // Pull-to-refresh on the tickets list (mobile touch gesture).
  const handlePullRefresh = useCallback(async () => {
    await Promise.all([fetchTickets(), fetchStats(), fetchLocationStats()]);
  }, [fetchTickets, fetchStats, fetchLocationStats]);
  const pull = usePullToRefresh(handlePullRefresh);
  const pullDashboard = usePullToRefresh(handlePullRefresh);
  const pullKanban = usePullToRefresh(handlePullRefresh);

  // Pull-to-refresh on the ticket detail conversation
  const handlePullRefreshDetail = useCallback(async () => {
    if (selectedTicketId) {
      await fetchTicketDetail(selectedTicketId);
    }
  }, [selectedTicketId, fetchTicketDetail]);
  const pullDetail = usePullToRefresh(handlePullRefreshDetail);

  useEffect(() => {
    fetchTickets();
    fetchLocations();
    fetchModules();
    fetchStats();
    fetchLocationStats();
    fetchAssignees();
  }, [fetchTickets, fetchLocations, fetchModules, fetchStats, fetchLocationStats, fetchAssignees]);

  // One-time swipe-actions hint on mobile for staff.
  useEffect(() => {
    if (!isStaff) return;
    try {
      const touch = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
      const small = typeof window !== 'undefined' && window.innerWidth < 768;
      if (!touch || !small) return;
      if (localStorage.getItem('crm-sync-swipe-hint-seen')) return;
      toast.info('Swipe tickets →', {
        description: 'Swipe right to assign to yourself · swipe left to resolve',
        duration: 6000,
      });
      localStorage.setItem('crm-sync-swipe-hint-seen', '1');
    } catch (e) { /* localStorage disabled or private mode — ignore */ }
  }, [isStaff]);

  // Don't auto-select first ticket in list view
  // useEffect(() => {
  //   if (!selectedTicketId && tickets.length > 0) {
  //     setSelectedTicketId(tickets[0].id);
  //   }
  // }, [tickets, selectedTicketId]);

  useEffect(() => {
    if (selectedTicketId) {
      fetchTicketDetail(selectedTicketId);
    }
  }, [selectedTicketId, fetchTicketDetail]);

  useEffect(() => {
    if (!selectedTicket) return;
    // reset composer when switching tickets
    setShowComposer(false);
    setShowComposerOnly(false);
    setReplyMode('reply');
    setReplyText('');
    setComposeTo(selectedTicket.created_by_email || '');
    setComposeCc('');
    setComposeBcc('');
    setShowRecipientsExpanded(false);
    setShowBcc(false);
    setDraftSubject((selectedTicket.subject || '').trim() || `[${selectedTicket.ticket_number}] ${selectedTicket.module}`);
    setEditingSubject(false);
    setComposerFullscreen(false);
    setReplyAttachments([]);
    setReplyKind('reply');
  }, [selectedTicket]);

  const filteredTickets = useMemo(() => {
    const q = search.trim().toLowerCase();

    const ordered = [...tickets].sort((a, b) => {
      const aTs = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTs = new Date(b.updated_at || b.created_at || 0).getTime();
      return bTs - aTs;
    });

    // Client-side Not Resolved view
    let base = ordered;
    if (filterStatus === 'not_resolved') {
      base = ordered.filter((t) => t.status !== 'resolved');
    } else if (filterStatus === 'recent_reply') {
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      base = ordered.filter((t) => {
        const replies = Array.isArray(t.replies) ? t.replies : [];
        if (replies.length === 0) return false;
        const last = replies[replies.length - 1];
        const ts = new Date(last?.created_at || t.updated_at || 0).getTime();
        return ts >= cutoff;
      });
    }

    // Internal status filter (staff-only UI, but filter is safe to apply for everyone)
    if (filterInternalStatus && filterInternalStatus !== 'all') {
      if (filterInternalStatus === '__none__') {
        base = base.filter((t) => !t.internal_status);
      } else {
        base = base.filter((t) => (t.internal_status || '') === filterInternalStatus);
      }
    }

    if (!q) return base;

    return base.filter((t) => {
      const hay = [
        t.ticket_number,
        t.module,
        t.location_name,
        t.created_by_name,
        t.created_by_email,
        stripHtml(t.text),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [tickets, search, filterStatus, filterInternalStatus]);

  const handleCreateTicket = async () => {
    if (!newTicket.location_id || !newTicket.module || !newTicket.text) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const res = await axios.post(`${API}/tickets`, newTicket);
      toast.success('Ticket created');
      setShowCreateDialog(false);
      setNewTicket({
        location_id: '',
        subject: '',
        module: '',
        text: '',
        priority: 'medium',
        reported_person_name: '',
        attachments: [],
        followers: [],
        on_behalf_of_user_id: '',
        internal_only: false,
      });
      setReplyText('');
      await fetchTickets();
      await fetchStats();
      await fetchLocationStats();
      if (res.data?.id) setSelectedTicketId(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const sendEmail = async ({ to, cc, bcc, subject, html, attachments = [], replyType = 'reply', fromEmail = '', replyAsUserId = '' }) => {
    // Parse CC/BCC strings into arrays
    const ccList = cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : [];
    const bccList = bcc ? bcc.split(',').map(e => e.trim()).filter(Boolean) : [];

    const msg = html;
    await axios.post(`${API}/tickets/${selectedTicket.id}/replies`, {
      message: msg,
      attachments: attachments,
      cc: ccList,
      bcc: bccList,
      reply_type: replyType,
      from_email: fromEmail || '',
      reply_as_user_id: replyAsUserId || null,
    });
  };

  const handleAddReply = async () => {
    if (!selectedTicket?.id) return;
    if (!replyText.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setReplying(true);
    try {
      const subject = (draftSubject || '').trim() || (selectedTicket.subject || '').trim() || `[${selectedTicket.ticket_number}] ${selectedTicket.module}`;
      // Build email body: typed reply on top + signature + (for actual email replies only) the
      // full thread quoted below — matches Gmail/Outlook default reply behaviour.
      const includeThread = replyKind === 'reply' && replyMode !== 'forward';
      const threadHtml = includeThread ? buildQuotedThreadHtml() : '';
      // Substitute signature placeholders ({{ticket_number}}, {{customer_name}}, …).
      const substituteVars = (raw) =>
        String(raw || '')
          .replaceAll('{{ticket_number}}', selectedTicket.ticket_number || '')
          .replaceAll('{{customer_name}}', selectedTicket.created_by_name || '')
          .replaceAll('{{customer_email}}', selectedTicket.created_by_email || '')
          .replaceAll('{{location_name}}', selectedTicket.location_name || '')
          .replaceAll('{{module}}', selectedTicket.module || '')
          .replaceAll('{{user_name}}', user?.name || user?.email || '');
      // The user's signature is now pre-filled into the editor body by openComposer,
      // so we don't auto-append it again here. We just substitute placeholders
      // across whatever the user has in the editor (which may include the signature).
      const finalReplyHtml = substituteVars(replyText);
      const html = `
        <div>
          <div class="crm-rich-content" style="margin-top:8px;">${finalReplyHtml}</div>
          ${threadHtml}
        </div>
      `;

      await sendEmail({
        to: composeTo,
        cc: composeCc,
        bcc: composeBcc,
        subject,
        html,
        attachments: replyAttachments,
        replyType: replyKind,
        fromEmail: senderIdentity,
        replyAsUserId: replyAsUserId,
      });

      // Persist any newly entered CC/BCC emails as contacts for future autocomplete.
      try {
        const newEmails = [
          ...(composeCc || '').split(','),
          ...(composeBcc || '').split(','),
        ]
          .map((e) => e.trim())
          .filter((e) => e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
        if (newEmails.length > 0) {
          await axios.post(`${API}/email-contacts`, { emails: newEmails });
          fetchEmailContacts();
        }
      } catch { /* non-critical */ }

      // persist subject if edited
      if ((draftSubject || '').trim() && (draftSubject || '').trim() !== (selectedTicket.subject || '').trim()) {
        await axios.put(`${API}/tickets/${selectedTicket.id}`, { subject: (draftSubject || '').trim() });
      }

      // Apply "Then set status to…" if user picked one.
      if (statusAfterSend && statusAfterSend !== selectedTicket.status) {
        try {
          await axios.put(`${API}/tickets/${selectedTicket.id}`, { status: statusAfterSend });
        } catch { /* non-critical */ }
      }

      toast.success(
        replyKind === 'private_note' ? 'Private note added' :
        replyKind === 'public_note' ? 'Public note added' :
        'Message sent'
      );
      setReplyText('');
      setComposeTo('');
      setComposeCc('');
      setComposeBcc('');
      setShowComposer(false);
      setShowComposerOnly(false);
      setShowRecipientsExpanded(false);
      setShowBcc(false);
      setEditingSubject(false);
      setComposerFullscreen(false);
      setReplyAttachments([]);
      setReplyKind('reply');
      setStatusAfterSend('');

      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
      await fetchStats();
      await fetchLocationStats();
    } catch (e) {
      toast.error('Failed to send');
    } finally {
      setReplying(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedTicket?.id) return;
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/tickets/${selectedTicket.id}`, { status: newStatus });
      toast.success('Status updated');
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
      await fetchStats();
      await fetchLocationStats();
    } catch (e) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleReopenTicket = async () => {
    if (!selectedTicket?.id) return;
    setUpdatingStatus(true);
    try {
      await axios.post(`${API}/tickets/${selectedTicket.id}/reopen`);
      toast.success('Ticket reopened');
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
      await fetchStats();
      await fetchLocationStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reopen ticket');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAssignTicket = async (assigneeId) => {
    if (!selectedTicket?.id) return;
    try {
      await axios.put(`${API}/tickets/${selectedTicket.id}`, { assigned_to_id: assigneeId });
      toast.success('Assignee updated');
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
    } catch (e) {
      toast.error('Failed to update assignee');
    }
  };

  // Quick row-level actions for mobile swipe gestures.
  const handleQuickAssignToMe = async (ticketId) => {
    if (!ticketId || !user?.id) return;
    try {
      await axios.put(`${API}/tickets/${ticketId}`, { assigned_to_id: user.id });
      toast.success(`Assigned to you · ${user.name || 'Me'}`);
      await fetchTickets();
      await fetchStats();
    } catch (e) {
      toast.error('Failed to assign');
    }
  };

  const handleQuickResolve = async (ticketId) => {
    if (!ticketId) return;
    try {
      await axios.put(`${API}/tickets/${ticketId}`, { status: 'resolved' });
      toast.success('Ticket resolved');
      await fetchTickets();
      await fetchStats();
      await fetchLocationStats();
    } catch (e) {
      toast.error('Failed to resolve');
    }
  };

  const handleUpdateInternalStatus = async (newId) => {
    if (!selectedTicket?.id) return;
    try {
      await axios.put(`${API}/tickets/${selectedTicket.id}`, { internal_status: newId || '' });
      toast.success('Internal status updated');
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update internal status');
    }
  };

  const handleUpdateDueDate = async (iso) => {
    if (!selectedTicket?.id) return;
    try {
      await axios.put(`${API}/tickets/${selectedTicket.id}`, { due_date: iso || '' });
      toast.success(iso ? 'Due date updated' : 'Due date cleared');
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update due date');
    }
  };

  const handleUpdatePriority = async (newPriority) => {
    if (!selectedTicket?.id) return;
    try {
      await axios.put(`${API}/tickets/${selectedTicket.id}`, { priority: newPriority });
      toast.success('Priority updated');
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
      await fetchStats();
      await fetchLocationStats();
    } catch (e) {
      toast.error('Failed to update priority');
    }
  };

  const handleUpdateModule = async (newModule) => {
    if (!selectedTicket?.id || !newModule) return;
    try {
      await axios.put(`${API}/tickets/${selectedTicket.id}`, { module: newModule });
      toast.success('Module updated');
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
    } catch (e) {
      toast.error('Failed to update module');
    }
  };

  const handleUpdateModules = async (newModules) => {
    if (!selectedTicket?.id) return;
    try {
      await axios.put(`${API}/tickets/${selectedTicket.id}`, { modules: newModules });
      toast.success('Modules updated');
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
    } catch (e) {
      toast.error('Failed to update modules');
    }
  };

  const handleUpdateTicketType = async (newType) => {
    if (!selectedTicket?.id) return;
    try {
      await axios.put(`${API}/tickets/${selectedTicket.id}`, { ticket_type: newType });
      toast.success('Ticket type updated');
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
    } catch (e) {
      toast.error('Failed to update ticket type');
    }
  };

  const handleUpdateFollowers = async (followers) => {
    if (!selectedTicket?.id) return;
    try {
      await axios.put(`${API}/tickets/${selectedTicket.id}`, { followers });
      toast.success('Followers updated');
      await fetchTicketDetail(selectedTicket.id);
      await fetchTickets();
    } catch (e) {
      toast.error('Failed to update followers');
    }
  };

  const handleAssignTicketInline = async (ticketId, assigneeId) => {
    try {
      await axios.put(`${API}/tickets/${ticketId}`, { assigned_to_id: assigneeId });
      toast.success('Assignee updated');
      await fetchTickets();
      if (selectedTicketId === ticketId) {
        await fetchTicketDetail(ticketId);
      }
    } catch (e) {
      toast.error('Failed to update assignee');
    }
  };

  const handleUpdateStatusInline = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API}/tickets/${ticketId}`, { status: newStatus });
      toast.success('Status updated');
      await fetchTickets();
      await fetchStats();
      await fetchLocationStats();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const handleReopenTicketInline = async (ticketId) => {
    try {
      await axios.post(`${API}/tickets/${ticketId}/reopen`);
      toast.success('Ticket reopened');
      await fetchTickets();
      await fetchStats();
      await fetchLocationStats();
      if (selectedTicketId === ticketId) await fetchTicketDetail(ticketId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reopen ticket');
    }
  };

  const handleSelectTicket = (ticketId) => {
    setSelectedTicketId(ticketId);
    setShowTicketView(true);
  };

  const handleBackToList = () => {
    setShowTicketView(false);
    setShowComposerOnly(false);
    // keep selection so returning to detail is instant
  };

  const buildQuotedThreadHtml = () => {
    if (!selectedTicket) return '';

    // Gmail-style quoted thread: each prior message rendered inside a left-bar
    // blockquote with grey background so it's clearly separate from the new reply.
    const quoteWrap = (innerHtml) =>
      `<blockquote style="margin:0 0 8px 0; padding:8px 12px; border-left:3px solid #cbd5e1; background:#f8fafc; color:#334155; border-radius:0 6px 6px 0;">${innerHtml}</blockquote>`;

    const parts = [];
    parts.push(`
      <div style="margin-top:18px; padding-top:14px; border-top:1px solid #e2e8f0;">
        <div style="font-size:12px; color:#64748b; font-style:italic; margin-bottom:6px;">— Previous messages —</div>
      </div>
    `);

    const replies = [...(selectedTicket.replies || [])].sort(
      (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );

    replies.forEach((r) => {
      const roleLabel = ['admin', 'super_admin'].includes(r.user_role) ? ' (Support)' : '';
      const header = `<div style="font-size:12px; color:#64748b; margin-bottom:4px;">On ${formatDateTime(r.created_at)} <strong>${r.user_name}${roleLabel}</strong> wrote:</div>`;
      parts.push(quoteWrap(header + (r.message || '')));
    });

    const baseHeader = `<div style="font-size:12px; color:#64748b; margin-bottom:4px;">On ${formatDateTime(selectedTicket.created_at)} <strong>${selectedTicket.created_by_name}</strong> &lt;${selectedTicket.created_by_email || ''}&gt; wrote:</div>`;
    parts.push(quoteWrap(baseHeader + (selectedTicket.text || '')));

    return parts.join('');
  };

  const openComposer = (mode, { kind = 'reply' } = {}) => {
    if (!selectedTicket) return;
    setReplyMode(mode);
    setReplyKind(kind);
    const isNoteMode = kind === 'public_note' || kind === 'private_note';

    if (mode === 'forward') {
      setComposeTo('');
    } else {
      setComposeTo(selectedTicket.created_by_email || '');
    }

    setShowRecipientsExpanded(false);
    setShowBcc(false);
    setDraftSubject((selectedTicket.subject || '').trim() || `[${selectedTicket.ticket_number}] ${selectedTicket.module}`);
    setEditingSubject(false);

    // Notes are internal, not emailed — skip the signature auto-fill.
    // Emails get the signature pre-filled so the agent can edit it.
    const sigRaw = (user?.email_signature || '').trim();
    if (!isNoteMode && sigRaw && mode !== 'forward') {
      // Wrap the signature in a marker block so the "Reply as" dropdown can
      // find it later and swap in the target user's signature without
      // touching the rest of the draft.
      setReplyText(`<p></p>${buildSignatureBlock(sigRaw)}`);
    } else {
      setReplyText('');
    }

    setShowComposer(true);
    setShowComposerOnly(true);

    setTimeout(() => {
      const el = document.querySelector('[data-testid="ticket-composer-only"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleReply = () => openComposer('reply');
  const handleReplyAll = () => openComposer('reply_all');
  const handleForward = () => openComposer('forward');
  // Open the composer directly in note mode — skips email recipients & thread.
  const handlePublicNote = () => openComposer('reply', { kind: 'public_note' });
  const handlePrivateNote = () => openComposer('reply', { kind: 'private_note' });

  const handleComposerBackToTicket = () => {
    setShowComposer(false);
    setShowComposerOnly(false);
    setReplyText('');
    setShowRecipientsExpanded(false);
    setShowBcc(false);
    setEditingSubject(false);
    setComposerFullscreen(false);
    setReplyAttachments([]);
  };

  // Handle pasting image in composer
  const handleComposerPasteImage = async (file, insertAt) => {
    try {
      const mt = (file.type || '').toLowerCase();
      if (!mt.startsWith('image/')) return;
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Pasted image exceeds 5MB limit');
        return;
      }

      const init = await axios.post(`${API}/uploads/init`, {
        filename: file.name || 'pasted-image.png',
        mime_type: file.type,
        size: file.size,
      });
      const uploadId = init.data.upload_id;
      const chunkSize = init.data.chunk_size;
      const totalChunks = Math.ceil(file.size / chunkSize);

      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
        const form = new FormData();
        form.append('file', chunk, file.name || 'pasted-image.png');
        await axios.post(`${API}/uploads/${uploadId}/chunk?chunk_index=${i}`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      const complete = await axios.post(`${API}/uploads/${uploadId}/complete?total_chunks=${totalChunks}`);
      const attachment = complete.data.attachment;

      // Inline-only paste: image lives in the body, NOT in the attachments
      // list. The email service auto-discovers the underlying file from disk
      // when it sees the /api/uploads/{id}/download URL in the HTML and
      // attaches it via CID — the recipient still sees the image inline.
      if (typeof insertAt === 'function') {
        insertAt(attachment);
      } else {
        setReplyText((prev) => `${prev || ''}<p><img src="${attachment.url}" alt="${attachment.filename}" /></p>`);
      }
      toast.success('Image inserted');
    } catch (e) {
      toast.error('Failed to upload pasted image');
    }
  };

  // Handle file attachments for composer
  const handleComposerAttachments = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const f of files) {
      const mt = (f.type || '').toLowerCase();
      const max = mt.startsWith('image/') ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
      if (f.size > max) {
        toast.error(`File too large: ${f.name}`);
        return;
      }
      if (!(mt.startsWith('image/') || mt.startsWith('video/') || mt === 'application/pdf' || mt.includes('document') || mt.includes('spreadsheet') || mt.includes('presentation'))) {
        toast.error(`Unsupported file type: ${f.name}`);
        return;
      }
    }

    try {
      const uploaded = [];
      for (const f of files) {
        const init = await axios.post(`${API}/uploads/init`, { filename: f.name, mime_type: f.type, size: f.size });
        const uploadId = init.data.upload_id;
        const chunkSize = init.data.chunk_size;
        const totalChunks = Math.ceil(f.size / chunkSize);
        for (let i = 0; i < totalChunks; i++) {
          const chunk = f.slice(i * chunkSize, Math.min((i + 1) * chunkSize, f.size));
          const form = new FormData();
          form.append('file', chunk, f.name);
          await axios.post(`${API}/uploads/${uploadId}/chunk?chunk_index=${i}`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
        const complete = await axios.post(`${API}/uploads/${uploadId}/complete?total_chunks=${totalChunks}`);
        uploaded.push(complete.data.attachment);
      }
      setReplyAttachments((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file(s) attached`);
      e.target.value = '';
    } catch (err) {
      toast.error('Upload failed');
    }
  };

  // Remove attachment from composer
  const handleRemoveReplyAttachment = (attachmentId) => {
    setReplyAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  // Ticket List View (Default)
  if (!showTicketView) {
    return (
      <div className="h-screen flex flex-col overflow-x-hidden" data-testid="tickets-page">
        {/* Header */}
        <div className="border-b border-slate-200 bg-white px-3 sm:px-4 py-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <h1 className="text-base sm:text-lg font-semibold text-slate-900 whitespace-nowrap">
                {viewMode === 'dashboard' ? 'Dashboard' : 'Support Tickets'}
              </h1>
              {/* Inline search — saves a whole filter row of vertical space */}
              {viewMode !== 'dashboard' && (
                <div className="relative flex-1 max-w-xs min-w-0">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search tickets…"
                    className="h-8 pl-8 text-sm"
                    data-testid="header-search"
                  />
                </div>
              )}
            </div>
            <div className="flex gap-1.5 items-center flex-wrap flex-shrink-0">
              {/* View toggle */}
              <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid="view-toggle">
                <Button
                  variant={viewMode === 'dashboard' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-8 px-2"
                  onClick={() => setViewMode('dashboard')}
                  data-testid="view-dashboard-btn"
                >
                  <BarChart3 className="w-4 h-4 lg:mr-1" /> <span className="hidden lg:inline">Dashboard</span>
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-8 px-2"
                  onClick={() => setViewMode('list')}
                  data-testid="view-list-btn"
                >
                  <ListIcon className="w-4 h-4 lg:mr-1" /> <span className="hidden lg:inline">List</span>
                </Button>
                <Button
                  variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-8 px-2"
                  onClick={() => setViewMode('kanban')}
                  data-testid="view-kanban-btn"
                >
                  <LayoutGrid className="w-4 h-4 lg:mr-1" /> <span className="hidden lg:inline">Kanban</span>
                </Button>
              </div>

              <Button
                onClick={() => {
                  fetchTickets();
                  fetchStats();
                  fetchLocationStats();
                }}
                variant="outline"
                size="sm"
                className="h-8 px-2"
                aria-label="Refresh"
                title="Refresh"
                data-testid="refresh-list-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button onClick={() => setShowCreateDialog(true)} data-testid="create-ticket-btn" size="sm" className="h-8 px-2.5">
                <Plus className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">New Ticket</span>
                <span className="sm:hidden">New</span>
              </Button>
            </div>
          </div>

          {/* Stats Summary — horizontal-scroll carousel on mobile, wrapping strip on desktop */}
          {stats && viewMode !== 'dashboard' && (
            <div
              className="mt-2 rounded-md border border-slate-200 bg-white px-3 py-2 relative"
              data-testid="stats-strip"
            >
              {/* Edge fade hints — mobile only */}
              <div
                className="pointer-events-none absolute top-0 bottom-0 left-0 w-6 bg-gradient-to-r from-white to-transparent rounded-l-md md:hidden z-10"
                aria-hidden="true"
              />
              <div
                className="pointer-events-none absolute top-0 bottom-0 right-0 w-6 bg-gradient-to-l from-white to-transparent rounded-r-md md:hidden z-10"
                aria-hidden="true"
              />
              <div
                className="
                  flex gap-1.5 items-center
                  overflow-x-auto md:flex-wrap md:overflow-visible
                  snap-x snap-mandatory md:snap-none
                  -mx-1 px-1
                  [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]
                "
              >
                <button
                  type="button"
                  onClick={() => handleClickStatusPill('open')}
                  className="snap-start flex-shrink-0"
                  data-testid="stat-pill-open"
                >
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 hover:bg-blue-100 cursor-pointer text-xs whitespace-nowrap">
                    Open: {stats.open}
                  </Badge>
                </button>
                <button
                  type="button"
                  onClick={() => handleClickStatusPill('in_progress')}
                  className="snap-start flex-shrink-0"
                  data-testid="stat-pill-in-progress"
                >
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 hover:bg-yellow-100 cursor-pointer text-xs whitespace-nowrap">
                    In Progress: {stats.in_progress}
                  </Badge>
                </button>
                <button
                  type="button"
                  onClick={() => handleClickStatusPill('waiting_on_customer')}
                  className="snap-start flex-shrink-0"
                  data-testid="stat-pill-waiting"
                >
                  <Badge variant="outline" className="bg-orange-50 text-orange-700 hover:bg-orange-100 cursor-pointer text-xs whitespace-nowrap">
                    Waiting: {stats.waiting_on_customer || 0}
                  </Badge>
                </button>
                <button
                  type="button"
                  onClick={() => handleClickStatusPill('resolved')}
                  className="snap-start flex-shrink-0"
                  data-testid="stat-pill-resolved"
                >
                  <Badge variant="outline" className="bg-green-50 text-green-700 hover:bg-green-100 cursor-pointer text-xs whitespace-nowrap">
                    Resolved: {stats.resolved}
                  </Badge>
                </button>
                <button
                  type="button"
                  onClick={handleClickNotResolved}
                  className="snap-start flex-shrink-0"
                  data-testid="stat-pill-not-resolved"
                >
                  <Badge variant="outline" className="bg-slate-50 text-slate-700 hover:bg-slate-100 cursor-pointer text-xs whitespace-nowrap">
                    Not Resolved: {notResolvedCount}
                  </Badge>
                </button>
                <button
                  type="button"
                  onClick={() => setFilterStatus('recent_reply')}
                  className="snap-start flex-shrink-0"
                  data-testid="stat-pill-recent-reply"
                  title="Tickets that received a reply in the last 24 hours"
                >
                  <Badge variant="outline" className="bg-cyan-50 text-cyan-700 hover:bg-cyan-100 cursor-pointer text-xs whitespace-nowrap">
                    Recent Reply (24h): {recentReplyCount}
                  </Badge>
                </button>
                <button
                  type="button"
                  onClick={handleClickTotal}
                  className="snap-start flex-shrink-0"
                  data-testid="stat-pill-total"
                >
                  <Badge variant="outline" className="bg-slate-900 text-white border-slate-900 hover:opacity-90 cursor-pointer text-xs whitespace-nowrap">
                    Total: {stats.total}
                  </Badge>
                </button>

                {locationStats?.length > 0 && (
                  <>
                    <div className="h-4 w-px bg-slate-200 mx-1 flex-shrink-0 hidden sm:block" />
                    {locationStats.map((ls) => {
                      const notResolved = (ls.open || 0) + (ls.in_progress || 0) + (ls.waiting_on_customer || 0) + (ls.closed || 0);
                      return (
                        <button
                          key={ls.location_id}
                          type="button"
                          onClick={() => handleClickLocationNotResolved(ls.location_id)}
                          className="snap-start flex-shrink-0 text-left"
                          data-testid={`stat-pill-loc-${ls.location_id}`}
                        >
                          <Badge variant="outline" className="bg-white hover:bg-slate-50 cursor-pointer text-xs whitespace-nowrap">
                            {ls.location_name}: {notResolved}
                          </Badge>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Filters */}
          {viewMode !== 'dashboard' && (
          <div className="mt-2 space-y-2">
            {/* Open | Resolved tabs + filters toggle (desktop + mobile) */}
            <div className="flex items-center justify-between gap-2">
              <div className="inline-flex rounded-full bg-slate-100 p-0.5" data-testid="open-resolved-tabs">
                {[
                  { key: 'open', label: 'Open', count: notResolvedCount, color: 'text-indigo-700' },
                  { key: 'resolved', label: 'Resolved', count: stats?.resolved || 0, color: 'text-emerald-700' },
                ].map((tab) => {
                  const active = openTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => handleSelectOpenTab(tab.key)}
                      className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                        active
                          ? `bg-white shadow-sm ${tab.color}`
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                      data-testid={`open-resolved-tab-${tab.key}`}
                    >
                      <span>{tab.label}</span>
                      <span className={`text-[11px] rounded-full px-1.5 py-0.5 ${
                        active ? 'bg-slate-100 text-slate-700' : 'bg-slate-200 text-slate-600'
                      }`}>{tab.count}</span>
                    </button>
                  );
                })}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9"
                onClick={() => setShowFilters((v) => !v)}
                data-testid="filters-toggle"
              >
                <Filter className="w-4 h-4 mr-1" />
                {showFilters ? 'Hide filters' : 'Filters'}
              </Button>
            </div>

            <div className={`flex flex-wrap gap-2 items-end ${showFilters ? 'flex' : 'hidden'}`}>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-40 h-9" data-testid="filter-status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="not_resolved">Not Resolved</SelectItem>
                <SelectItem value="recent_reply">Recent Reply (24h)</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                <SelectItem value="under_investigation">Under Investigation</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterLocation} onValueChange={setFilterLocation}>
              <SelectTrigger className="w-full md:w-40 h-9" data-testid="filter-location">
                <SelectValue placeholder="All locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-full md:w-40 h-9" data-testid="filter-priority">
                <SelectValue placeholder="All priorities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>

            {isStaff && internalStatuses.length > 0 && (
              <Select value={filterInternalStatus} onValueChange={setFilterInternalStatus}>
                <SelectTrigger className="w-full md:w-48 h-9" data-testid="filter-internal-status">
                  <SelectValue placeholder="All internal statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All internal statuses</SelectItem>
                  <SelectItem value="__none__">— No internal status —</SelectItem>
                  {internalStatuses.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.label || s.name || s.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Input 
              type="date" 
              value={dateFrom} 
              onChange={(e) => setDateFrom(e.target.value)} 
              className="flex-1 min-w-[140px] md:flex-initial md:w-36 h-9"
              data-testid="filter-date-from" 
            />
            <Input 
              type="date" 
              value={dateTo} 
              onChange={(e) => setDateTo(e.target.value)} 
              className="flex-1 min-w-[140px] md:flex-initial md:w-36 h-9"
              data-testid="filter-date-to" 
            />

            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() => {
                setFilterStatus('open');
                setFilterLocation('all');
                setFilterPriority('all');
                setFilterInternalStatus('all');
                setDateFrom('');
                setDateTo('');
                setSearch('');
              }}
            >
              Clear
            </Button>

            {viewMode === 'kanban' ? (
              <Select value={kanbanGroupBy} onValueChange={setKanbanGroupBy}>
                <SelectTrigger className="w-full md:w-44 h-9" data-testid="kanban-groupby">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="status">Group by: Status</SelectItem>
                  <SelectItem value="priority">Group by: Priority</SelectItem>
                  <SelectItem value="assignee">Group by: Assignee</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Select
                value={listGroupBy}
                onValueChange={(v) => {
                  setListGroupBy(v);
                  setListGroupFilter('all');
                }}
              >
                <SelectTrigger className="w-full md:w-44 h-9" data-testid="list-groupby">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No grouping</SelectItem>
                  <SelectItem value="status">Group by: Status</SelectItem>
                  <SelectItem value="priority">Group by: Priority</SelectItem>
                  <SelectItem value="assignee">Group by: Team member</SelectItem>
                </SelectContent>
              </Select>
            )}
            </div>
          </div>
          )}
        </div>

        {/* Tickets Table / Kanban */}
        <div className="flex-1 overflow-hidden flex flex-col">

          {viewMode === 'dashboard' ? (
            <div
              {...pullDashboard.pullProps}
              className="h-full overflow-auto relative overscroll-contain"
              style={{
                transform: pullDashboard.pullDistance > 0 ? `translateY(${pullDashboard.pullDistance}px)` : undefined,
                transition: pullDashboard.pullDistance > 0 ? 'none' : 'transform 0.2s ease',
              }}
              data-testid="tickets-dashboard-scroll"
            >
              <PullToRefreshIndicator
                distance={pullDashboard.pullDistance}
                threshold={pullDashboard.threshold}
                refreshing={pullDashboard.isRefreshing}
              />
              <TicketDashboard locations={locations} />
            </div>
          ) : viewMode === 'kanban' ? (
            <div
              {...pullKanban.pullProps}
              className="h-full overflow-y-auto relative overscroll-contain"
              style={{
                transform: pullKanban.pullDistance > 0 ? `translateY(${pullKanban.pullDistance}px)` : undefined,
                transition: pullKanban.pullDistance > 0 ? 'none' : 'transform 0.2s ease',
              }}
              data-testid="tickets-kanban-scroll"
            >
              <PullToRefreshIndicator
                distance={pullKanban.pullDistance}
                threshold={pullKanban.threshold}
                refreshing={pullKanban.isRefreshing}
              />
              <TicketKanban
                tickets={filteredTickets}
                groupBy={kanbanGroupBy}
                assignees={assignees}
                onSelectTicket={(id) => handleSelectTicket(id)}
                internalStatuses={internalStatuses}
              />
            </div>
          ) : (
          <div
            {...pull.pullProps}
            className="h-full overflow-auto px-4 py-2 relative overscroll-contain"
            style={{
              transform: pull.pullDistance > 0 ? `translateY(${pull.pullDistance}px)` : undefined,
              transition: pull.pullDistance > 0 ? 'none' : 'transform 0.2s ease',
            }}
            data-testid="tickets-list-scroll"
          >
            <PullToRefreshIndicator
              distance={pull.pullDistance}
              threshold={pull.threshold}
              refreshing={pull.isRefreshing}
            />
            {filteredTickets.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Ticket className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <div className="text-lg font-medium text-slate-700">No tickets found</div>
                  <div className="text-slate-500 mt-1">Try adjusting your filters or create a new ticket.</div>
                </div>
              </div>
            ) : (() => {
              const renderRow = (ticket) => (
                <SwipeTableRow
                  key={ticket.id}
                  className="cursor-pointer"
                  data-testid={`ticket-row-${ticket.id}`}
                  onSwipeRight={isStaff ? () => handleQuickAssignToMe(ticket.id) : undefined}
                  onSwipeLeft={isStaff ? () => handleQuickResolve(ticket.id) : undefined}
                >
                  <TableCell
                    className="hidden md:table-cell font-medium text-slate-900"
                    onClick={() => handleSelectTicket(ticket.id)}
                  >
                    {ticket.location_name}
                  </TableCell>
                  <TableCell
                    className="hidden lg:table-cell"
                    onClick={() => handleSelectTicket(ticket.id)}
                  >
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs whitespace-nowrap max-w-full truncate inline-block">
                      {ticket.module}
                    </Badge>
                  </TableCell>
                  <TableCell onClick={() => handleSelectTicket(ticket.id)}>
                    <Badge variant="outline" className="bg-slate-900 text-white border-slate-900 font-mono text-xs whitespace-nowrap">
                      #{ticket.ticket_number}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-900" onClick={() => handleSelectTicket(ticket.id)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      {ticket.customer_replied_unread && (
                        <span
                          className="relative inline-flex items-center"
                          title="Customer replied — unread"
                          data-testid="unread-dot"
                        >
                          <span className="absolute inline-flex h-2 w-2 rounded-full bg-rose-500 opacity-75 animate-ping" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
                        </span>
                      )}
                      <span className={`truncate ${ticket.customer_replied_unread ? 'font-semibold' : ''}`}>
                        {(ticket.subject || '').trim() || stripHtml(ticket.text).slice(0, 80) || '(no subject)'}
                      </span>
                      {ticket.internal_only && (
                        <Badge
                          variant="outline"
                          className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] font-semibold gap-1"
                          title="Agency-only ticket"
                          data-testid="row-internal-badge"
                        >
                          <span className="w-1 h-1 rounded-full bg-amber-500" />
                          INTERNAL
                        </Badge>
                      )}
                      {ticket.on_behalf_of_actor_name && (
                        <Badge
                          variant="outline"
                          className="bg-violet-50 text-violet-700 border-violet-200 text-[10px]"
                          title={`Created by ${ticket.on_behalf_of_actor_name} on behalf of ${ticket.created_by_name}`}
                          data-testid="row-onbehalf-badge"
                        >
                          on behalf of {ticket.created_by_name}
                        </Badge>
                      )}
                      {(ticket.sla_breached || ticket.sla_paused) && (
                        <SlaBadge
                          breached={ticket.sla_breached}
                          paused={ticket.sla_paused}
                          dueAt={ticket.sla_due_at}
                          hours={ticket.sla_hours}
                        />
                      )}
                      {(() => {
                        const ci = internalStatuses.find((i) => i.id === ticket.internal_status);
                        if (!ci) return null;
                        return (
                          <Badge
                            variant="outline"
                            className={`${internalStatusBadgeClasses(ci.color)} text-[10px] gap-1`}
                            title={`Internal status: ${ci.label}`}
                            data-testid="row-internal-status-badge"
                          >
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                            {ci.label}
                          </Badge>
                        );
                      })()}
                      {ticket.due_date && (() => {
                        const overdue = new Date(ticket.due_date).getTime() < Date.now() && ticket.status !== 'resolved';
                        return (
                          <Badge
                            variant="outline"
                            className={`${overdue
                              ? 'bg-rose-50 text-rose-700 border-rose-200'
                              : 'bg-sky-50 text-sky-700 border-sky-200'} text-[10px] gap-1`}
                            title={`Estimated delivery: ${formatDueDate(ticket.due_date)} (${dueDateRelative(ticket.due_date)})`}
                            data-testid="row-due-date-badge"
                          >
                            <Clock className="w-3 h-3" />
                            {formatDueDate(ticket.due_date)}
                          </Badge>
                        );
                      })()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                      <Select value={ticket.status} onValueChange={(v) => handleUpdateStatusInline(ticket.id, v)}>
                        <SelectTrigger className="h-8 flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                          <SelectItem value="under_investigation">Under Investigation</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                      {ticket.status === 'resolved' && (isStaff() || ticket.created_by_id === user?.id) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 flex-shrink-0"
                          title="Reopen ticket"
                          onClick={() => handleReopenTicketInline(ticket.id)}
                          data-testid={`reopen-ticket-inline-${ticket.id}`}
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Select
                      value={ticket.assigned_to_id || 'unassigned'}
                      onValueChange={(v) => handleAssignTicketInline(ticket.id, v === 'unassigned' ? '' : v)}
                    >
                      <div onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                      </div>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {assignees.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name || a.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="hidden xl:table-cell text-center" onClick={() => handleSelectTicket(ticket.id)}>
                    <div className="inline-flex items-center gap-1 text-slate-600">
                      <MessageSquare className="w-4 h-4" />
                      <span className="text-sm">{ticket.replies?.length || 0}</span>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-slate-600 text-xs whitespace-nowrap" onClick={() => handleSelectTicket(ticket.id)}>
                    {(() => {
                      const replies = Array.isArray(ticket.replies) ? ticket.replies : [];
                      if (replies.length === 0) return <span className="text-slate-400">—</span>;
                      const last = replies[replies.length - 1];
                      return formatRelativeTime(last?.created_at || ticket.updated_at);
                    })()}
                  </TableCell>
                  <TableCell className="hidden md:table-cell" onClick={() => handleSelectTicket(ticket.id)}>
                    <PriorityBadge priority={ticket.priority} />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-slate-600" onClick={() => handleSelectTicket(ticket.id)}>
                    {formatDateCompact(ticket.created_at)}
                  </TableCell>
                </SwipeTableRow>
              );

              const headerRow = (
                <TableHeader>
                  <TableRow>
                    <TableHead className="hidden md:table-cell w-[150px]">Location</TableHead>
                    <TableHead className="hidden lg:table-cell w-[180px]">Module</TableHead>
                    <TableHead className="w-[120px]">Ticket #</TableHead>
                    <TableHead className="min-w-[200px] md:min-w-[360px]">Subject</TableHead>
                    <TableHead className="w-[140px]">Status</TableHead>
                    <TableHead className="hidden lg:table-cell w-[200px]">Assignee</TableHead>
                    <TableHead className="hidden xl:table-cell w-[85px] text-center">Replies</TableHead>
                    <TableHead className="hidden lg:table-cell w-[120px]">Last Reply</TableHead>
                    <TableHead className="hidden md:table-cell w-[105px]">Priority</TableHead>
                    <TableHead className="hidden lg:table-cell w-[150px]">Created</TableHead>
                  </TableRow>
                </TableHeader>
              );

              if (listGroupBy === 'none') {
                return (
                  <Table>
                    {headerRow}
                    <TableBody>{filteredTickets.map(renderRow)}</TableBody>
                  </Table>
                );
              }

              // If a specific tab is selected, show a flat table of just that group
              if (listGroupFilter !== 'all') {
                const matching = filteredTickets.filter((t) => {
                  if (listGroupBy === 'status') return t.status === listGroupFilter;
                  if (listGroupBy === 'priority') return t.priority === listGroupFilter;
                  const tid = t.assigned_to_id || 'unassigned';
                  return tid === listGroupFilter;
                });
                if (matching.length === 0) {
                  return (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <Ticket className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <div className="text-lg font-medium text-slate-700">No tickets in this group</div>
                      </div>
                    </div>
                  );
                }
                return (
                  <Table>
                    {headerRow}
                    <TableBody>{matching.map(renderRow)}</TableBody>
                  </Table>
                );
              }

              // Group tickets by chosen dimension
              const priorityOrder = ['critical', 'high', 'medium', 'low'];
              const statusOrder = ['open', 'in_progress', 'waiting_on_customer', 'under_investigation', 'resolved'];
              const buckets = new Map();
              filteredTickets.forEach((t) => {
                let key;
                let label;
                if (listGroupBy === 'status') {
                  key = t.status || 'unknown';
                  label = {
                    open: 'Open',
                    in_progress: 'In Progress',
                    waiting_on_customer: 'Waiting on Customer',
                    under_investigation: 'Under Investigation',
                    resolved: 'Resolved',
                  }[key] || 'Unknown';
                } else if (listGroupBy === 'priority') {
                  key = t.priority || 'medium';
                  label = key.charAt(0).toUpperCase() + key.slice(1);
                } else {
                  // assignee / team member
                  key = t.assigned_to_id || 'unassigned';
                  label = t.assigned_to_name || t.assigned_to_email || 'Unassigned';
                }
                if (!buckets.has(key)) buckets.set(key, { key, label, tickets: [] });
                buckets.get(key).tickets.push(t);
              });

              const groupArr = Array.from(buckets.values());
              if (listGroupBy === 'status') {
                groupArr.sort((a, b) => statusOrder.indexOf(a.key) - statusOrder.indexOf(b.key));
              } else if (listGroupBy === 'priority') {
                groupArr.sort((a, b) => priorityOrder.indexOf(a.key) - priorityOrder.indexOf(b.key));
              } else {
                groupArr.sort((a, b) => {
                  if (a.key === 'unassigned') return 1;
                  if (b.key === 'unassigned') return -1;
                  return a.label.localeCompare(b.label);
                });
              }

              return (
                <div className="space-y-3 p-2">
                  {groupArr.map((g) => {
                    // Status-bucket coloured header chips so "Open / In Progress / Resolved" stand out.
                    const statusColor = {
                      open: 'bg-amber-100 text-amber-800 border-amber-200',
                      in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
                      waiting_on_customer: 'bg-purple-100 text-purple-800 border-purple-200',
                      under_investigation: 'bg-cyan-100 text-cyan-800 border-cyan-200',
                      resolved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
                    };
                    const headerCls = listGroupBy === 'status' && statusColor[g.key]
                      ? statusColor[g.key]
                      : 'bg-slate-100 text-slate-800 border-slate-200';
                    return (
                      <div
                        key={g.key}
                        className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm"
                        data-testid={`list-group-${g.key}`}
                      >
                        <div className={`px-4 py-2.5 border-b border-slate-200 flex items-center justify-between ${
                          listGroupBy === 'status' ? 'bg-gradient-to-r from-slate-50 to-white' : 'bg-slate-50'
                        }`}>
                          <div className="flex items-center gap-2.5">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${headerCls}`}
                              data-testid={`list-group-header-${g.key}`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                              {g.label}
                            </span>
                            <Badge variant="outline" className="bg-white text-slate-700 font-semibold">
                              {g.tickets.length}
                            </Badge>
                          </div>
                        </div>
                        <Table>
                          {headerRow}
                          <TableBody>{g.tickets.map(renderRow)}</TableBody>
                        </Table>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          )}
        </div>

        {/* Create Ticket Dialog */}
        <CreateTicketDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          newTicket={newTicket}
          setNewTicket={setNewTicket}
          locations={locations}
          availableModules={availableModules}
          assignees={assignees}
          isStaff={isStaff}
          isAgencyStaff={isAgencyStaff}
          isAgencyAdmin={isAgencyAdmin}
          creating={creating}
          onCreate={handleCreateTicket}
          ticketTemplates={ticketTemplates}
        />
      </div>
    );
  }

  // 3-Panel Ticket View
  return (
    <div className="h-screen flex flex-col overflow-x-hidden" data-testid="tickets-page">
      <TicketDetailHeader
        selectedTicket={selectedTicket}
        isStaff={isStaff}
        user={user}
        updatingStatus={updatingStatus}
        showRightPanel={showRightPanel}
        setShowRightPanel={setShowRightPanel}
        setMobileDetailOpen={setMobileDetailOpen}
        onBack={handleBackToList}
        onReopen={handleReopenTicket}
        onUpdateStatus={handleUpdateStatus}
        onRefresh={() => { fetchTickets(); fetchStats(); fetchLocationStats(); }}
      />

      {/* 3-Panel Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Ticket List (collapsible) — hidden on mobile when a ticket is open */}
        {ticketsListCollapsed ? (
          <div className="w-10 border-r border-slate-200 bg-white flex-col items-center py-3 hidden lg:flex">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setTicketsListCollapsed(false)}
              title="Show tickets list"
              data-testid="tickets-list-expand"
            >
              <ChevronRight className="w-4 h-4 text-slate-500" />
            </Button>
            <div className="text-[10px] font-medium text-slate-400 mt-3 [writing-mode:vertical-lr] rotate-180">
              Tickets ({filteredTickets.length})
            </div>
          </div>
        ) : (
        <div className="w-80 border-r border-slate-200 bg-white flex-col hidden lg:flex">
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-slate-900">Tickets</h3>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-slate-50 text-slate-700">
                  {filteredTickets.length}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setTicketsListCollapsed(true)}
                  title="Collapse tickets list"
                  data-testid="tickets-list-collapse"
                >
                  <ChevronLeft className="w-4 h-4 text-slate-500" />
                </Button>
              </div>
            </div>
            <Input 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              placeholder="Search..." 
              className="h-8 text-sm"
            />
          </div>
          
          <div className="flex-1 overflow-auto">
            {filteredTickets.map((ticket) => (
              <TicketListItem
                key={ticket.id}
                ticket={ticket}
                selected={ticket.id === selectedTicketId}
                onSelect={() => setSelectedTicketId(ticket.id)}
                compact={true}
                // left sidebar list: display-only UI
                showInlineStatus={false}
                showInlineAssignee={false}
                showAssignee={true}
              />
            ))}
          </div>
        </div>
        )}


        {/* Composer-only 2-pane mode (Ticket list + Composer) - Can be fullscreen */}
        {selectedTicket && showComposerOnly && (
          <TicketComposerPane
            selectedTicket={selectedTicket}
            composerFullscreen={composerFullscreen}
            setComposerFullscreen={setComposerFullscreen}
            replyText={replyText}
            setReplyText={setReplyText}
            replying={replying}
            onSendReply={handleAddReply}
            onUpdateStatus={handleUpdateStatus}
            onAssignTicket={handleAssignTicket}
            assignees={assignees}
            composeTo={composeTo}
            setComposeTo={setComposeTo}
            composeCc={composeCc}
            setComposeCc={setComposeCc}
            composeBcc={composeBcc}
            setComposeBcc={setComposeBcc}
            showRecipientsExpanded={showRecipientsExpanded}
            setShowRecipientsExpanded={setShowRecipientsExpanded}
            showBcc={showBcc}
            setShowBcc={setShowBcc}
            emailContacts={emailContacts}
            editingSubject={editingSubject}
            setEditingSubject={setEditingSubject}
            draftSubject={draftSubject}
            setDraftSubject={setDraftSubject}
            replyAttachments={replyAttachments}
            onPasteImage={handleComposerPasteImage}
            onPickAttachments={handleComposerAttachments}
            onRemoveAttachment={handleRemoveReplyAttachment}
            replyKind={replyKind}
            setReplyKind={setReplyKind}
            statusAfterSend={statusAfterSend}
            setStatusAfterSend={setStatusAfterSend}
            replyMode={replyMode}
            onClose={handleComposerBackToTicket}
            isStaff={isStaff}
            quotedThreadHtml={buildQuotedThreadHtml()}
            replySnippets={replySnippets}
            onInsertSnippet={(s) => setReplyText((prev) => `${prev || ''}${s.body || ''}`)}
            aiBusy={aiBusy}
            onAiSuggest={handleAiSuggest}
            onUpdateFollowers={handleUpdateFollowers}
            senderIdentities={senderIdentities}
            senderIdentity={senderIdentity}
            setSenderIdentity={setSenderIdentity}
            replyAsUserId={replyAsUserId}
            setReplyAsUserId={setReplyAsUserId}
            currentUserId={user?.id}
          />
        )}

        {/* Middle Panel - Conversation */}
        {!showComposerOnly && (
          <div className={`flex-1 min-w-0 bg-slate-50 flex flex-col overflow-x-hidden ${showRightPanel ? '' : 'border-r border-slate-200'}`}>
          {!selectedTicket ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-slate-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <div className="text-lg font-medium">Select a ticket</div>
                <div className="text-sm">Choose a ticket from the list to view the conversation</div>
              </div>
            </div>
          ) : (
            <>
              {/* Subject line — sticky header so agents can see it while scrolling */}
              <div className="border-b border-slate-200 bg-white px-4 sm:px-5 py-3 flex-shrink-0" data-testid="ticket-subject-header">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">Subject</div>
                <div className="text-base font-semibold text-slate-900 mt-0.5 truncate" title={(selectedTicket.subject || '').trim() || selectedTicket.module}>
                  {(selectedTicket.subject || '').trim() || <span className="text-slate-400 italic">(no subject)</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  #{selectedTicket.ticket_number} · {selectedTicket.module} · {selectedTicket.location_name}
                </div>
              </div>

                            {/* Conversation Messages */}
              <div
                {...pullDetail.pullProps}
                className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 space-y-1 relative overscroll-contain"
                style={{
                  transform: pullDetail.pullDistance > 0 ? `translateY(${pullDetail.pullDistance}px)` : undefined,
                  transition: pullDetail.pullDistance > 0 ? 'none' : 'transform 0.2s ease',
                }}
                data-testid="ticket-conversation-scroll"
              >
                <PullToRefreshIndicator
                  distance={pullDetail.pullDistance}
                  threshold={pullDetail.threshold}
                  refreshing={pullDetail.isRefreshing}
                />
                {(() => {
                  // Build a unified, reverse-chronological list (newest first).
                  // The very first card is expanded by default; everything older is
                  // collapsed (click to expand) — matches Gmail/Zoho ticket conversations.
                  const items = [];
                  items.push({
                    key: 'original',
                    ts: selectedTicket.created_at,
                    align: 'left',
                    title: selectedTicket.created_by_name,
                    subtitle: formatDateTime(selectedTicket.created_at),
                    html: selectedTicket.text,
                    attachments: selectedTicket.attachments,
                    replyType: undefined,
                  });
                  (selectedTicket.replies || []).forEach((reply) => {
                    items.push({
                      key: reply.id,
                      ts: reply.created_at,
                      align: ['admin', 'super_admin'].includes(reply.user_role) ? 'right' : 'left',
                      title: `${reply.user_name}${['admin', 'super_admin'].includes(reply.user_role) ? ' (Support)' : ''}`,
                      subtitle: formatDateTime(reply.created_at),
                      html: reply.message,
                      attachments: reply.attachments,
                      replyType: reply.reply_type,
                    });
                  });
                  items.sort((a, b) => new Date(b.ts || 0).getTime() - new Date(a.ts || 0).getTime());
                  return items.map((m) => (
                    <ConversationMessage
                      key={m.key}
                      align={m.align}
                      title={m.title}
                      subtitle={m.subtitle}
                      html={m.html}
                      attachments={m.attachments}
                      replyType={m.replyType}
                      defaultExpanded={false}
                      onReply={handleReply}
                      onReplyAll={handleReplyAll}
                      onForward={handleForward}
                    />
                  ));
                })()}
              </div>

              {/* Reply Section */}
              {selectedTicket.status !== 'resolved' && (
                <div className="bg-white border-t border-slate-200 p-4" data-testid="ticket-composer">
                  <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                    <div className="text-sm font-medium text-slate-700">Reply</div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant={replyMode === 'reply' ? 'default' : 'outline'}
                        size="sm"
                        onClick={handleReply}
                        data-testid="mode-reply"
                      >
                        <Reply className="w-3.5 h-3.5 mr-1" />
                        Reply
                      </Button>
                      <Button
                        variant={replyMode === 'reply_all' ? 'default' : 'outline'}
                        size="sm"
                        onClick={handleReplyAll}
                        data-testid="mode-reply-all"
                      >
                        <ReplyAll className="w-3.5 h-3.5 mr-1" />
                        Reply all
                      </Button>
                      {isStaff() && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePublicNote}
                            className="border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                            data-testid="mode-public-note-btn"
                            title="Public note — visible to customer, no email sent"
                          >
                            <StickyNote className="w-3.5 h-3.5 mr-1" />
                            Public note
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePrivateNote}
                            className="border-slate-300 text-slate-700 hover:bg-slate-100"
                            data-testid="mode-private-note-btn"
                            title="Private note — staff only, no email sent"
                          >
                            <StickyNote className="w-3.5 h-3.5 mr-1" />
                            Private note
                          </Button>
                        </>
                      )}
                      <Button
                        variant={replyMode === 'forward' ? 'default' : 'outline'}
                        size="sm"
                        onClick={handleForward}
                        data-testid="mode-forward"
                      >
                        <Forward className="w-3.5 h-3.5 mr-1" />
                        Forward
                      </Button>
                    </div>
                  </div>

                  {showComposer && (
                    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-3 shadow-sm">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-slate-500">From</Label>
                          <Input value="crmsupport@ottawaheroes.ca" disabled />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Subject</Label>
                          <Input value={`[${selectedTicket.ticket_number}] ${selectedTicket.module}`} disabled />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs text-slate-500">To</Label>
                        <Input value={composeTo} onChange={(e) => setComposeTo(e.target.value)} placeholder="email@example.com" />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-slate-500">Cc</Label>
                          <Input value={composeCc} onChange={(e) => setComposeCc(e.target.value)} placeholder="comma separated" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Bcc</Label>
                          <Input value={composeBcc} onChange={(e) => setComposeBcc(e.target.value)} placeholder="comma separated" />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs text-slate-500">Message</Label>
                        <RichTextEditor
                          content={replyText}
                          onChange={(html) => setReplyText(html)}
                          placeholder="Write your message"
                          minHeight={140}
                        />
                      </div>

                      <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                        <div className="text-xs font-medium text-slate-600">Thread</div>
                        <div className="mt-2 text-xs text-slate-600 prose prose-sm prose-slate max-w-none">
                          <p className="text-slate-500">Included below in the draft.</p>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowComposer(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleAddReply} disabled={replying || !replyText.trim()} data-testid="send-reply">
                          {replying ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                          Send
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        )}


        {/* Right Panel - Ticket Properties — desktop inline, mobile slide-over */}
        {selectedTicket && !showComposerOnly && (
          <>
            {/* Desktop inline sidebar */}
            {showRightPanel && (
              <div className="hidden lg:flex h-full">
                <TicketDetailSidebar
                  selectedTicket={selectedTicket}
                  isStaff={isStaff}
                  assignees={assignees}
                  availableModulesForEdit={availableModulesForEdit}
                  internalStatuses={internalStatuses}
                  onClose={() => setShowRightPanel(false)}
                  onUpdateStatus={handleUpdateStatus}
                  onUpdatePriority={handleUpdatePriority}
                  onUpdateModules={handleUpdateModules}
                  onUpdateTicketType={handleUpdateTicketType}
                  onAssignTicket={handleAssignTicket}
                  onOpenAllAttachments={() => setShowAllAttachments(true)}
                  onUpdateInternalStatus={handleUpdateInternalStatus}
                  onUpdateDueDate={handleUpdateDueDate}
                  onUpdateFollowers={handleUpdateFollowers}
                />
              </div>
            )}
            {/* Mobile slide-over drawer (independent of desktop toggle) */}
            {mobileDetailOpen && (
              <div className="lg:hidden fixed inset-0 z-40 flex" data-testid="mobile-detail-overlay">
                <div
                  className="absolute inset-0 bg-slate-900/40"
                  onClick={() => setMobileDetailOpen(false)}
                  aria-hidden="true"
                />
                <div className="ml-auto h-full w-[85%] max-w-sm bg-white shadow-xl relative z-10 flex">
                  <TicketDetailSidebar
                    selectedTicket={selectedTicket}
                    isStaff={isStaff}
                    assignees={assignees}
                    availableModulesForEdit={availableModulesForEdit}
                    internalStatuses={internalStatuses}
                    onClose={() => setMobileDetailOpen(false)}
                    onUpdateStatus={handleUpdateStatus}
                    onUpdatePriority={handleUpdatePriority}
                    onUpdateModules={handleUpdateModules}
                    onUpdateTicketType={handleUpdateTicketType}
                    onAssignTicket={handleAssignTicket}
                    onOpenAllAttachments={() => setShowAllAttachments(true)}
                    onUpdateInternalStatus={handleUpdateInternalStatus}
                    onUpdateDueDate={handleUpdateDueDate}
                    onUpdateFollowers={handleUpdateFollowers}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <AllAttachmentsDialog
        open={showAllAttachments}
        onOpenChange={setShowAllAttachments}
        ticket={selectedTicket}
      />

      {/* Mobile Reply FAB — quick access to composer when scrolled into a long thread */}
      {selectedTicket && !showComposerOnly && !showComposer && (
        <button
          type="button"
          onClick={handleReply}
          className="lg:hidden fixed bottom-5 right-5 z-30 h-14 w-14 rounded-full bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 active:bg-indigo-800 transition-all flex items-center justify-center"
          data-testid="mobile-reply-fab"
          title="Reply"
          aria-label="Reply to this ticket"
        >
          <Reply className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default TicketsInboxPage;